import React, { useEffect, useState } from 'react';
import { X, Check } from 'lucide-react';
import { THEMES, PAGE_W, PAGE_H } from '../lib/dossierThemes';
import { CoverPage } from './CoverPage';
import { applyColorOverride } from '../lib/colorPalettes';
import ColorPaletteSelector from './ColorPaletteSelector';

// ───────────────────────────────────────────────────────────────────────────
// TemplateGalleryModal — full-screen template picker with live A4 mini-previews.
//
// Each card renders the actual <CoverPage> at full 794×1123, scaled to ~26%
// via CSS transform — so the user sees a true visual preview, not a static
// thumbnail. Click a card to apply that template and close the modal.
// ───────────────────────────────────────────────────────────────────────────

// Sample data shown in every mini preview so the user can compare layouts
// at a glance with realistic content.
const SAMPLE_DATA = {
  name: 'Sophie Martin',
  title: 'Senior Manager — Stratégie & Transformation',
  profile: {
    years_of_experience: 12,
    phone: '+33 6 12 34 56 78',
    email: 'sophie.martin@cabinet.fr',
  },
};

const SCALE = 0.26;
const PREVIEW_W = Math.round(PAGE_W * SCALE);   // 207px
const PREVIEW_H = Math.round(PAGE_H * SCALE);   // 292px

// ── Single template card ────────────────────────────────────────────────────
const TemplateCard = ({ themeKey, theme, isCurrent, onSelect }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={() => onSelect(themeKey)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor: 'pointer',
        borderRadius: 12,
        overflow: 'hidden',
        background: '#fff',
        border: `2px solid ${isCurrent ? theme.switchAccent : (hovered ? '#cbd5e1' : '#e5e7eb')}`,
        boxShadow: hovered
          ? '0 12px 32px rgba(0,0,0,0.15)'
          : '0 2px 8px rgba(0,0,0,0.06)',
        transition: 'all 0.18s ease',
        transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
        position: 'relative',
      }}
    >
      {/* "Active" badge for the currently-applied template */}
      {isCurrent && (
        <div style={{
          position: 'absolute',
          top: 8, right: 8,
          zIndex: 5,
          background: theme.switchAccent,
          color: '#fff',
          fontSize: 10, fontWeight: 700,
          padding: '4px 8px',
          borderRadius: 12,
          display: 'flex', alignItems: 'center', gap: 4,
          boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
        }}>
          <Check size={10} /> Actif
        </div>
      )}

      {/* Mini A4 preview — full CoverPage at SCALE */}
      <div style={{
        width: PREVIEW_W,
        height: PREVIEW_H,
        background: '#f3f4f6',
        overflow: 'hidden',
        position: 'relative',
      }}>
        <div style={{
          transform: `scale(${SCALE})`,
          transformOrigin: 'top left',
          width: PAGE_W,
          height: PAGE_H,
        }}>
          <CoverPage
            theme={theme}
            name={SAMPLE_DATA.name}
            title={SAMPLE_DATA.title}
            profile={SAMPLE_DATA.profile}
            editable={false}
          />
        </div>
      </div>

      {/* Card footer: label + description + colour swatches */}
      <div style={{ padding: '12px 14px', borderTop: '1px solid #f3f4f6' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{theme.label}</div>
          <div style={{ display: 'flex', gap: 3 }}>
            <ColourSwatch color={theme.pageBorder} />
            <ColourSwatch color={theme.accentBar} />
          </div>
        </div>
        <div style={{
          fontSize: 11,
          color: '#6b7280',
          lineHeight: 1.35,
          height: 30,           // 2-line clamp
          overflow: 'hidden',
        }}>
          {theme.description || ''}
        </div>
      </div>
    </div>
  );
};

const ColourSwatch = ({ color }) => (
  <div style={{
    width: 12, height: 12,
    borderRadius: 3,
    background: color,
    border: '1px solid rgba(0,0,0,0.06)',
  }} />
);

// ── Main modal ──────────────────────────────────────────────────────────────
const TemplateGalleryModal = ({
  open,
  currentTemplate,
  colorOverride = null,
  onColorOverrideChange,
  onSelect,
  onClose,
}) => {
  // Lock body scroll while modal is open + close on Escape
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleSelect = (key) => {
    onSelect(key);
    onClose();
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 24px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 16,
          width: '100%',
          maxWidth: 1240,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 28px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 2 }}>
              Bibliothèque de templates
            </div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              Cliquez sur un template pour l'appliquer immédiatement à votre dossier.
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Fermer"
            style={{
              width: 36, height: 36,
              borderRadius: 8,
              border: '1px solid #e5e7eb',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#6b7280',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#111827'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#6b7280'; }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Color customization bar — applies to every template preview below */}
        {onColorOverrideChange && (
          <div style={{
            padding: '14px 28px',
            borderBottom: '1px solid #e5e7eb',
            background: '#fff',
          }}>
            <ColorPaletteSelector
              value={colorOverride}
              onChange={onColorOverrideChange}
              onClear={() => onColorOverrideChange(null)}
            />
          </div>
        )}

        {/* Grid of template cards */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px 28px 28px',
          background: '#fafbfc',
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${PREVIEW_W + 4}px, 1fr))`,
            gap: 20,
          }}>
            {Object.entries(THEMES).map(([key, theme]) => (
              <TemplateCard
                key={key}
                themeKey={key}
                theme={applyColorOverride(theme, colorOverride)}
                isCurrent={key === currentTemplate}
                onSelect={handleSelect}
              />
            ))}
          </div>
        </div>

        {/* Footer hint */}
        <div style={{
          padding: '12px 28px',
          borderTop: '1px solid #e5e7eb',
          background: '#fafbfc',
          fontSize: 11,
          color: '#9ca3af',
          textAlign: 'center',
        }}>
          Aperçus en taille réelle — {Object.keys(THEMES).length} templates disponibles. Échap pour fermer.
        </div>
      </div>
    </div>
  );
};

export default TemplateGalleryModal;
